import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  catchError,
  defer,
  finalize,
  map,
  merge,
  Observable,
  of,
  Subscription,
  tap
} from 'rxjs';
import { POSTHOG_KEYS } from 'src/app/constants/posthog-keys.const';
import { Plantation } from 'src/app/models/crd-state.interface';
import { EvidencePlotCount } from 'src/app/models/evidence.model';
import { chunkPlotCodes } from 'src/app/utils/plot-review.util';
import { EvidenceService } from '../data-service/evidence.service';
import { PosthogService } from '../posthog.service';

/**
 * Counts and share links for the Evidence column. Counts are fetched only
 * while the column is shown, since each request scans BigQuery and calls
 * AgriTrace.
 */
@Injectable({
  providedIn: 'root'
})
export class EvidenceStateService {
  private readonly api = inject(EvidenceService);
  private readonly posthogService = inject(PosthogService);

  readonly enabled = toSignal(
    this.posthogService.getFeatureFlag(POSTHOG_KEYS.EVIDENCE_COLLECTION),
    { initialValue: false }
  );

  private readonly crd = signal('');
  private readonly codes = signal<readonly string[]>([]);
  private readonly columnShown = signal(false);
  private readonly counts = signal<ReadonlyMap<string, number | null>>(
    new Map()
  );
  private readonly links = signal<ReadonlyMap<string, string>>(new Map());
  private readonly minting = signal<ReadonlySet<string>>(new Set());
  private loadSub?: Subscription;

  constructor() {
    effect(() => {
      const crd = this.crd();
      const codes = this.codes();
      const active = this.enabled() && this.columnShown();
      untracked(() => this.load(crd, codes, active));
    });
  }

  /** The plots now on screen in `crd`. */
  loadForPlantations(crd: string, plantations: Plantation[]): void {
    if (crd !== this.crd()) {
      this.counts.set(new Map());
      this.links.set(new Map());
      this.minting.set(new Set());
      this.crd.set(crd);
    }
    this.codes.set([
      ...new Set(
        plantations.map((p) => p.plantation_code).filter((code) => !!code)
      )
    ]);
  }

  /**
   * Since the service stays alive after the table closes and keeps the last
   * page's codes, this empties the stored plot codes, so no counts request is
   * sent until the table loads a new page.
   */
  clearPage(): void {
    this.codes.set([]);
  }

  setColumnShown(shown: boolean): void {
    this.columnShown.set(shown);
  }

  /** undefined while loading, null when a store did not answer. */
  countFor(plantationCode: string): number | null | undefined {
    return this.counts().get(plantationCode);
  }

  linkFor(plantationCode: string): string | null {
    return this.links().get(plantationCode) ?? null;
  }

  isMinting(plantationCode: string): boolean {
    return this.minting().has(plantationCode);
  }

  /** The plot's evidence page URL, minted on first use. */
  mintLink(plantationCode: string): Observable<string> {
    const crd = this.crd();
    return defer(() => {
      this.setMinting(plantationCode, true);
      return this.api.mintLink(plantationCode);
    }).pipe(
      map((link) => link.url),
      tap((url) => {
        if (this.crd() === crd) {
          this.links.update((current) =>
            new Map(current).set(plantationCode, url)
          );
        }
      }),
      finalize(() => this.setMinting(plantationCode, false))
    );
  }

  private load(crd: string, codes: readonly string[], active: boolean): void {
    this.loadSub?.unsubscribe();
    if (!active || !crd || codes.length === 0) {
      return;
    }

    // merge, not forkJoin, so one slow chunk does not hold back the others.
    this.loadSub = merge(
      ...chunkPlotCodes([...codes]).map((chunk) =>
        this.api.getCounts(chunk).pipe(
          map((results) => countsForChunk(chunk, results)),
          catchError(() => of(chunk.map((code) => [code, null] as const)))
        )
      )
    ).subscribe((entries) => {
      if (this.crd() !== crd) {
        return;
      }
      this.counts.update((current) => {
        const next = new Map(current);
        for (const [code, count] of entries) {
          next.set(code, count);
        }
        return next;
      });
    });
  }

  private setMinting(plantationCode: string, minting: boolean): void {
    this.minting.update((current) => {
      const next = new Set(current);
      if (minting) {
        next.add(plantationCode);
      } else {
        next.delete(plantationCode);
      }
      return next;
    });
  }
}

/** A code missing from the answer is unknown, not zero. */
function countsForChunk(
  chunk: string[],
  results: EvidencePlotCount[]
): (readonly [string, number | null])[] {
  const byCode = new Map(
    results.map((entry) => [entry.plantation_code, entry.count])
  );
  return chunk.map((code) => [code, byCode.get(code) ?? null] as const);
}
